/**
 * Lire la face d'un dé, et choisir celle qui sortira — sans jamais le retoucher.
 *
 * Au 421, le résultat est tiré par le serveur (`shared/hasard.ts`). La physique ne
 * décide de rien : elle met en scène. Reste le problème délicat — le dé doit
 * rouler librement **et** finir sur la face demandée.
 *
 * ## Ce qui a changé, et pourquoi
 *
 * La première version faisait rouler le dé, attendait l'immobilité, puis le
 * faisait pivoter vers la face voulue. Ça se voyait : le dé posé tournait encore.
 * Un ajustement « minimal » atteint quand même 90° quand la face demandée est sur
 * le côté, et 180° quand elle est contre le tapis.
 *
 * On prend le problème par l'autre bout : on choisit la face **au départ**, par une
 * rotation appliquée à l'orientation initiale. Deux propriétés du cube le
 * permettent sans toucher au mouvement :
 *
 *   - ses 24 rotations propres permutent ses sommets, donc le collider occupe
 *     exactement le même volume à chaque instant ;
 *   - l'inertie d'un cube homogène est isotrope (I = c·Id), donc invariante par
 *     rotation.
 *
 * Rien dans la dynamique ne change ; seule l'étiquette des faces bouge. Si le jet
 * naturel finit sur F et qu'on veut V, on part de `orientation ∘ S` avec S la
 * symétrie qui envoie la normale de V sur celle de F.
 *
 * ⚠️ En théorie la trajectoire est identique au bit près. En pratique elle ne l'est
 * pas : les sommets du collider sont énumérés dans un autre ordre, le solveur
 * séquentiel de rapier accumule ses impulsions différemment, et un dé qui rebondit
 * est chaotique — mesuré, 2,5 % des jets bifurquent, et franchement (plus d'un
 * mètre d'écart). C'est pourquoi `lancer.ts` ne se contente jamais du calcul : il
 * simule, vérifie la face obtenue, et recommence tant qu'elle n'est pas la bonne.
 */

import { Quaternion, Vector3 } from "three";

export type Face = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Normale de chaque face dans le repère du dé. Doit rester cohérente avec le
 * placement des faces du maillage (voir De.tsx) : une incohérence ferait afficher
 * la mauvaise valeur tout en étant « physiquement » correcte.
 */
export const NORMALES: Readonly<Record<Face, readonly [number, number, number]>> = {
  1: [0, 0, 1],
  6: [0, 0, -1],
  3: [1, 0, 0],
  4: [-1, 0, 0],
  5: [0, 1, 0],
  2: [0, -1, 0],
};

export const FACES: readonly Face[] = [1, 2, 3, 4, 5, 6];

/** Face tournée vers le haut pour une orientation donnée. */
export function faceVisible(orientation: Quaternion): Face {
  let meilleure: Face = 1;
  let meilleurY = -Infinity;
  for (const face of FACES) {
    const n = NORMALES[face];
    const v = new Vector3(n[0], n[1], n[2]).applyQuaternion(orientation);
    if (v.y > meilleurY) {
      meilleurY = v.y;
      meilleure = face;
    }
  }
  return meilleure;
}

/** Vecteur normale d'une face, en repère du dé. */
export function normale(face: Face): Vector3 {
  const n = NORMALES[face];
  return new Vector3(n[0], n[1], n[2]);
}

/**
 * Symétrie du cube amenant la normale de `de` sur celle de `vers`.
 *
 * Les six normales étant des axes de coordonnées, les trois cas possibles donnent
 * tous une rotation du groupe du cube : l'identité, un quart de tour autour d'un
 * axe de coordonnées, ou un demi-tour. C'est ce qui garantit que le collider est
 * inchangé — `estSymetrieDuCube` le vérifie en test.
 */
export function symetrieFaceVersFace(de: Face, vers: Face): Quaternion {
  const a = normale(de);
  const b = normale(vers);
  const produit = a.dot(b);
  if (produit > 0.999) return new Quaternion();
  if (produit < -0.999) {
    // Faces opposées : demi-tour autour d'un axe de coordonnées perpendiculaire.
    const axe = Math.abs(a.x) < 0.5 ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0);
    return new Quaternion().setFromAxisAngle(axe, Math.PI);
  }
  return new Quaternion().setFromAxisAngle(
    new Vector3().crossVectors(a, b).normalize(), Math.PI / 2);
}

/**
 * Vrai si la rotation permute les six normales du cube — c'est-à-dire si elle
 * laisse le collider invariant. Condition nécessaire et suffisante pour qu'une
 * pré-orientation ne change pas la physique.
 */
export function estSymetrieDuCube(rotation: Quaternion): boolean {
  const normales = FACES.map(normale);
  return FACES.every((face) => {
    const image = normale(face).applyQuaternion(rotation);
    return normales.some((n) => image.distanceTo(n) < 1e-9);
  });
}
